'use client';

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { getSequenceByIdService, getSequenceChangelogService, getSequenceListService } from '@/services/httpServices/SequenceServices';
import type { GetSequenceByIdServiceConfig, SequenceByIdResponse, SequenceChangelogResponse, SequenceChangelogServiceConfig, SequenceListResponse, SequenceListServiceConfig } from '@/services/httpServices/SequenceServices/types';
import type { ChangelogEntry } from '@/types/changelog';

import { buildStandardPaginatedParams } from './createInfiniteQuery';
import { infiniteSequenceListKeys, sequenceByIdKeys, sequenceChangelogKeys, sequenceListKeys } from './sequenceQueryKeys';
import { useQueryConfig } from './useQueryConfig';

export function useSequenceQuery(params: SequenceListServiceConfig, enabled: boolean = true) {
  const { isEnabled } = useQueryConfig();

  return useQuery<SequenceListResponse>({
    queryKey: sequenceListKeys(params),
    queryFn: async () => getSequenceListService(params),
    enabled: isEnabled && enabled,
  });
}

/**
 * Hook to fetch sequence list with infinite scrolling using cursor-based pagination
 * @param initialParams - Initial sequence list query parameters
 * @param enabled - Whether to enable the query
 * @returns Infinite query result for the sequence list
 */
export function useInfiniteSequenceQuery(
  initialParams: SequenceListServiceConfig,
  enabled: boolean = true,
) {
  const { isEnabled } = useQueryConfig();

  return useInfiniteQuery<SequenceListResponse>({
    queryKey: infiniteSequenceListKeys(initialParams),
    queryFn: async ({ pageParam }) => {
      const paginationParams = buildStandardPaginatedParams(
        initialParams,
        pageParam ? String(pageParam) : undefined,
      );
      return getSequenceListService(paginationParams);
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      if (lastPage.pageInfo.hasNextPage === true && lastPage.pageInfo.endCursor !== null) {
        return lastPage.pageInfo.endCursor;
      }
      return undefined;
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.pageInfo.hasPreviousPage === true && firstPage.pageInfo.startCursor !== null) {
        return firstPage.pageInfo.startCursor;
      }
      return undefined;
    },
    enabled: isEnabled && enabled,
  });
}

/**
 * Hook to fetch sequence data by ID
 * @param params - Sequence query parameters (optional)
 * @param enabled - Whether to enable the query (optional)
 * @returns Query result for the sequence
 */
export function useSequenceByIdQuery(
  params?: GetSequenceByIdServiceConfig,
  enabled?: boolean,
) {
  const { isEnabled } = useQueryConfig();

  return useQuery<SequenceByIdResponse>({
    queryKey: sequenceByIdKeys(params),
    queryFn: async () => {
      if (params === undefined) {
        throw new Error('Sequence params are required');
      }
      return getSequenceByIdService(params);
    },
    enabled: isEnabled && (enabled ?? (params !== undefined && params.params?.params.id !== undefined)),
    refetchOnWindowFocus: 'always',
  });
}

/**
 * Hook to fetch sequence changelog data
 * @param params - Sequence changelog parameters (optional)
 * @param enabled - Whether to enable the query (optional)
 * @returns Query result for the sequence changelog
 */
export function useSequenceChangelogQuery(
  params?: SequenceChangelogServiceConfig,
  enabled: boolean = true,
) {
  const { isEnabled } = useQueryConfig();

  return useQuery<ChangelogEntry[]>({
    queryKey: sequenceChangelogKeys(params),
    queryFn: async (): Promise<ChangelogEntry[]> => {
      if (params === undefined || params === null) {
        throw new Error('Sequence changelog params are required');
      }

      const response: SequenceChangelogResponse = await getSequenceChangelogService(params);

      return response.nodes || [];
    },
    enabled: isEnabled && enabled && params !== undefined,
  });
}
